import { LoginLinkDto } from 'libs/end-to-end/src/auth';
import { deserializeError } from 'libs/end-to-end/src/api-error';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { AuthLayout } from 'apps/sigma2-ui/src/components/AuthLayout';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { FormValidationError } from 'apps/sigma2-ui/src/components/ui/FormValidationError';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { uiEnvironment } from 'apps/sigma2-ui/src/constants/environment';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { apiCall } from 'apps/sigma2-ui/src/lib/api';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getStateWithContinuePath } from 'apps/sigma2-ui/src/state/state-with-continue-path';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { IconVisibility24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconVisibility24px';
import { IconVisibilityOff24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconVisibilityOff24px';
import { CircularProgress } from '@mui/material';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { RoutableLink } from 'apps/sigma2-ui/src/components/ui/RoutableLink';
import { useNavigate } from 'react-router';

const { callbackLoginUri } = uiEnvironment;

export const LoginPage = () => {
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const language = useLanguage();
  const location = useLocation();
  const navigate = useNavigateSafe();
  const navigateRaw = useNavigate();

  const { myUserStatus, login } = useUser({
    onError: (loginError) => {
      let errorText = language.errorGeneric();

      if (loginError.errorType === 'InvalidCredentialsError') {
        errorText = language.authErrorUserNotFound();
      }

      setError(errorText);
      setIsSubmissionPending(false);
    },
  });

  const stateWithContinuePath = useMemo(
    () => getStateWithContinuePath(location.state),
    [location.state],
  );

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isPasswordShown, setPasswordShown] = useState<boolean>(false);
  const [isSubmissionPending, setIsSubmissionPending] =
    useState<boolean>(false);

  useEffect(() => {
    if (myUserStatus === 'logged_in') {
      navigateRaw(stateWithContinuePath.continuePath);
    }
  }, [myUserStatus, stateWithContinuePath, navigateRaw]);

  const isSubmitDisabled = useMemo(() => {
    if (isSubmissionPending || email.length === 0 || password.length === 0) {
      return true;
    }

    return false;
  }, [email, password, isSubmissionPending]);

  const [ssoEmailDomain, setSsoEmailDomain] = useState<string>('');
  const isLoginWithSSODisabled = useMemo(() => {
    if (ssoEmailDomain.length === 0) {
      return true;
    }

    return false;
  }, [ssoEmailDomain]);

  const [showLoginWithSsoDialog, setShowLoginWithSsoDialog] = useState(false);

  const submit = useAsyncCallback(
    async (isAlive) => {
      if (
        !isAlive() ||
        !email ||
        email.length === 0 ||
        !password ||
        password.length === 0 ||
        isSubmissionPending
      ) {
        return;
      }

      setIsSubmissionPending(true);
      login(email, password);
    },
    [email, password, isSubmissionPending, login],
  );

  const onLoginWithSSO = useAsyncCallback(
    async (isAlive, domainHint) => {
      if (!isAlive() || isSubmissionPending || isLoginWithSSODisabled) {
        return;
      }

      setIsSubmissionPending(true);

      const response = await apiCall<LoginLinkDto>({
        url: `${getApiBaseUrl()}/auth/sso/login-link/generate`,
        deserializeErrorFn: deserializeError,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({
            domainHint,
            redirectUri: `${callbackLoginUri}`,
          }),
        },
        responseSchema: LoginLinkDto,
      });
      if (response.resultType === 'err') {
        setIsSubmissionPending(false);
        return;
      }

      window.location.replace(response.value.link);
    },
    [isLoginWithSSODisabled, isSubmissionPending],
  );

  const ssoDialogUi = useMemo(() => {
    return (
      <Transition appear show={showLoginWithSsoDialog} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-overlay"
          onClose={() => setShowLoginWithSsoDialog(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Login with SSO
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Please type in your company domain.
                    </p>
                  </div>

                  <div className="mt-4">
                    <TextInput
                      data-qa="domain"
                      type="text"
                      onChange={(e) => setSsoEmailDomain(e.currentTarget.value)}
                    />

                    <LegacyButton
                      variant={LegacyButtonVariant.PRIMARY_LIGHT}
                      onClick={() => onLoginWithSSO(ssoEmailDomain)}
                      disabled={isLoginWithSSODisabled}
                      className="mt-4 ml-4"
                    >
                      Login with SSO
                    </LegacyButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }, [
    isLoginWithSSODisabled,
    onLoginWithSSO,
    showLoginWithSsoDialog,
    ssoEmailDomain,
  ]);

  const ui = useMemo(() => {
    return (
      <AuthLayout>
        <Helmet>
          <title>{documentTitle(language.logIn())}</title>
        </Helmet>
        {ssoDialogUi}
        <h1 className="-translate-y-full absolute font-normal pb-5 text-2xl text-gray-100 transform">
          {language.logIn()}
        </h1>
        {error && (
          <FormValidationError data-qa="serverError" className="mb-5">
            <p>{error}</p>
          </FormValidationError>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="text-left">
            <label htmlFor="email">{language.emailInputLabel()}</label>
            <TextInput
              ref={emailRef}
              id="email"
              type="email"
              name="email"
              data-qa="emailInput"
              onChange={(e) => setEmail(e.currentTarget.value)}
              autoFocus
              autoComplete="username"
              value={email}
              required
            />
          </div>
          <div className="relative mt-6 mb-1">
            <label htmlFor="password">{language.passwordInputLabel()}</label>
            <div className="relative">
              <TextInput
                ref={passwordRef}
                id="password"
                name="password"
                data-qa="passwordInput"
                onChange={(e) => setPassword(e.currentTarget.value)}
                type={isPasswordShown ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                required
              />
              <button
                data-qa="passwordVisibilityToggle"
                type="button"
                className="text-gray-900 hover:text-tertiary w-6 absolute mt-1.5 top-0 h-12 focus-visible:outline-none focus-visible:text-tertiary"
                style={{ bottom: '1rem', right: '1rem' }}
                title={isPasswordShown ? 'hide password' : 'show password'}
                onClick={() =>
                  setPasswordShown((prevPasswordShown) => !prevPasswordShown)
                }
                tabIndex={-1}
              >
                {isPasswordShown ? (
                  <IconVisibilityOff24pxSvg />
                ) : (
                  <IconVisibility24pxSvg />
                )}
              </button>
            </div>
          </div>

          <div>
            <RoutableLink
              className="text-white underline hover:no-underline focus:text-white focus:outline-none focus:ring-1 focus:ring-blue-300 px-2 -ml-1 rounded transition"
              to="/forgot-password"
              state={stateWithContinuePath}
            >
              {language.forgotYourPassword()}
            </RoutableLink>
          </div>

          <div className="flex justify-end mt-6 space-x-5">
            <LegacyButton
              type="submit"
              variant={LegacyButtonVariant.PRIMARY_DARK}
              disabled={isSubmitDisabled}
              className="w-[100px] items-center"
            >
              {isSubmissionPending ? (
                <CircularProgress
                  color="inherit"
                  className="text-secondary"
                  size={16}
                  thickness={4}
                />
              ) : (
                language.logIn()
              )}
            </LegacyButton>
            <LegacyButton
              type="button"
              onClick={() => setShowLoginWithSsoDialog(true)}
              variant={LegacyButtonVariant.PRIMARY_DARK}
            >
              {language.logInWithSSO()}
            </LegacyButton>
          </div>
        </form>
      </AuthLayout>
    );
  }, [
    language,
    ssoDialogUi,
    error,
    email,
    isPasswordShown,
    password,
    stateWithContinuePath,
    isSubmitDisabled,
    isSubmissionPending,
    submit,
  ]);

  return ui;
};
